import ButtonMUI from "@mui/material/Button";
import axios from "axios";
import moment from "moment";
import { FC, useRef, useState } from "react";
import FormControl from "react-bootstrap/FormControl";
import { toast } from "react-toastify";
import Url from "../../../../../util/url";

interface Attachment {
    _id: string;
    fileName: string;
    fileUrl: string;
    fileType?: string;
    uploadedBy?: string;
    uploadedAt?: string;
}

interface Props {
    taskId: string;
    initialAttachments: Attachment[];
    userId?: string;
}

const ICON_MAP: Record<string, { icon: string; bg: string; color: string }> = {
    image: { icon: "🖼️", bg: "#ecfdf3", color: "#027a48" },
    pdf: { icon: "📄", bg: "#fef3f2", color: "#b42318" },
    link: { icon: "🔗", bg: "#eff8ff", color: "#175cd3" },
    github: { icon: "", bg: "#f6f8fa", color: "#24292f" },
    doc: { icon: "📝", bg: "#eff8ff", color: "#175cd3" },
    md: { icon: "📋", bg: "#f4f3ff", color: "#5925dc" },
};
const DEFAULT_ICON = { icon: "📎", bg: "#f2f4f7", color: "#344054" };

const GITHUB_ISSUE_RE = /github\.com\/([^/]+\/[^/]+)\/issues\/(\d+)/;

const parseGitHubUrl = (url: string) => {
    const match = url.match(GITHUB_ISSUE_RE);
    if (!match) return null;
    return { repo: match[1], number: match[2] };
};

const GitHubIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/>
    </svg>
);

const AttachmentsPanel: FC<Props> = ({ taskId, initialAttachments, userId }) => {
    const [attachments, setAttachments] = useState<Attachment[]>(initialAttachments);
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");
    const [linkName, setLinkName] = useState("");
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const formData = new FormData();
        formData.append("file", file);
        formData.append("userId", userId ?? "");
        setUploading(true);
        try {
            const res = await axios.post(Url(`task/${taskId}/attachment`), formData);
            setAttachments(res.data);
            toast.success("Upload thành công");
        } catch (err: any) {
            toast.error(err.response?.data?.error || "Upload thất bại");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const handleAddLink = async () => {
        if (!linkUrl) return;
        const gh = parseGitHubUrl(linkUrl);
        const fileType = gh ? "github" : "link";
        const defaultName = gh ? `${gh.repo}#${gh.number}` : linkUrl;
        try {
            const res = await axios.post(Url(`task/${taskId}/attachment`), {
                url: linkUrl,
                fileName: linkName || defaultName,
                fileType,
                userId,
            });
            setAttachments(res.data);
            setLinkUrl("");
            setLinkName("");
            setShowLinkInput(false);
            toast.success("Thêm link thành công");
        } catch (err: any) {
            toast.error(err.response?.data?.error || "Thêm link thất bại");
        }
    };

    const handleDelete = async (attachmentId: string) => {
        try {
            const res = await axios.delete(Url(`task/${taskId}/attachment/${attachmentId}`));
            setAttachments(res.data);
            toast.success("Đã xóa");
        } catch {
            toast.error("Xóa thất bại");
        }
    };

    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <h5 style={{ margin: 0 }}>
                    Đính kèm{" "}
                    {attachments.length > 0 && (
                        <span style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>({attachments.length})</span>
                    )}
                </h5>
                <div style={{ display: "flex", gap: 6 }}>
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept=".pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.md,.html"
                        onChange={handleUploadFile}
                    />
                    <ButtonMUI
                        size="small"
                        variant="outlined"
                        disabled={uploading}
                        sx={{ textTransform: "none", fontSize: 12, borderColor: "#d0d5dd", color: "#344054" }}
                        startIcon={<span style={{ fontSize: 14 }}>+</span>}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {uploading ? "Đang upload..." : "Upload file"}
                    </ButtonMUI>
                    <ButtonMUI
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: "none", fontSize: 12, borderColor: "#d0d5dd", color: "#344054" }}
                        startIcon={<span style={{ fontSize: 14 }}>🔗</span>}
                        onClick={() => setShowLinkInput(!showLinkInput)}
                    >
                        Thêm link
                    </ButtonMUI>
                </div>
            </div>

            {showLinkInput && (
                <div style={{ display: "flex", gap: 8, marginBottom: 10, padding: 10, background: "#f8f9fa", borderRadius: 6 }}>
                    <FormControl
                        size="sm"
                        placeholder="Tên hiển thị"
                        value={linkName}
                        onChange={(e: any) => setLinkName(e.target.value)}
                        style={{ width: 140, fontSize: 13 }}
                    />
                    <FormControl
                        size="sm"
                        placeholder="URL (https://...)"
                        value={linkUrl}
                        onChange={(e: any) => setLinkUrl(e.target.value)}
                        style={{ flex: 1, fontSize: 13 }}
                    />
                    <ButtonMUI
                        size="small"
                        variant="contained"
                        disabled={!linkUrl}
                        sx={{ textTransform: "none", fontSize: 12 }}
                        onClick={handleAddLink}
                    >
                        Lưu
                    </ButtonMUI>
                    <ButtonMUI
                        size="small"
                        variant="text"
                        sx={{ textTransform: "none", fontSize: 12, color: "#667085", minWidth: "auto" }}
                        onClick={() => { setShowLinkInput(false); setLinkUrl(""); setLinkName(""); }}
                    >
                        Hủy
                    </ButtonMUI>
                </div>
            )}

            {attachments.length > 0 ? (
                <div style={{ border: "1px solid #eaecf0", borderRadius: 8, overflow: "hidden" }}>
                    {attachments.map((att, idx) => {
                        const style = ICON_MAP[att.fileType ?? ""] || DEFAULT_ICON;
                        const uploadDate = att.uploadedAt ? moment(att.uploadedAt).format("DD/MM/YYYY HH:mm") : "";
                        return (
                            <div
                                key={att._id}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    padding: "8px 12px",
                                    borderBottom: idx < attachments.length - 1 ? "1px solid #eaecf0" : "none",
                                    transition: "background 0.15s",
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                            >
                                <div
                                    style={{
                                        width: 32, height: 32, borderRadius: 6,
                                        background: style.bg, color: style.color,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: 16, flexShrink: 0,
                                    }}
                                >
                                    {att.fileType === "github" ? <GitHubIcon /> : style.icon}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <a
                                        href={att.fileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            fontSize: 13, fontWeight: 500, color: "#344054",
                                            textDecoration: "none", display: "block",
                                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.color = "#1570ef")}
                                        onMouseLeave={(e) => (e.currentTarget.style.color = "#344054")}
                                    >
                                        {att.fileName}
                                    </a>
                                    {uploadDate && <span style={{ fontSize: 11, color: "#98a2b3" }}>{uploadDate}</span>}
                                </div>
                                {att.fileType !== "link" && att.fileType !== "github" && (
                                    <ButtonMUI
                                        size="small"
                                        sx={{
                                            minWidth: 28, width: 28, height: 28, padding: 0,
                                            borderRadius: "6px", color: "#98a2b3",
                                            "&:hover": { background: "#eff8ff", color: "#175cd3" },
                                        }}
                                        onClick={async () => {
                                            try {
                                                const res = await fetch(att.fileUrl);
                                                const blob = await res.blob();
                                                const url = window.URL.createObjectURL(blob);
                                                const a = document.createElement("a");
                                                a.href = url;
                                                a.download = att.fileName;
                                                a.click();
                                                window.URL.revokeObjectURL(url);
                                            } catch {
                                                window.open(att.fileUrl, "_blank");
                                            }
                                        }}
                                    >
                                        ↓
                                    </ButtonMUI>
                                )}
                                <ButtonMUI
                                    size="small"
                                    sx={{
                                        minWidth: 28, width: 28, height: 28, padding: 0,
                                        borderRadius: "6px", color: "#98a2b3",
                                        "&:hover": { background: "#fee4e2", color: "#b42318" },
                                    }}
                                    onClick={() => handleDelete(att._id)}
                                >
                                    ✕
                                </ButtonMUI>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div
                    style={{
                        padding: "16px", textAlign: "center", fontSize: 13, color: "#98a2b3",
                        border: "1px dashed #d0d5dd", borderRadius: 8,
                    }}
                >
                    Chưa có file đính kèm
                </div>
            )}
        </div>
    );
};

export default AttachmentsPanel;
